import type { FC } from 'react';
import React from 'react';
import styled from 'styled-components';
import type { LegendSeries as SeriesProps } from './Legend';
import { PureNameWithOptionalFee } from './NameWithOptionalFee';
import { Icon } from 'venn-ui-kit';
import { useBlockSubjectSuperscripts } from '../blocks/components/core/block-subject-footnotes-and-superscripts';
import type { StudioRequestSubject } from 'venn-state';
import { isNil } from 'lodash';

export interface LegendEntryIndicatorProps {
  color: string;
  height?: number;
}

const LegendEntryWrapper = styled.div<{ fontSize?: string }>`
  font-size: ${({ fontSize }) => fontSize || '12px'};
  font-weight: bold;
  display: flex;
  align-items: center;

  & + div {
    margin-left: 20px;
  }
`;

export interface LegendEntryIndicatorWrapperProps {
  borderColor?: string;
  fillColor?: string;
}

const LegendEntryIndicatorWrapper = styled.div<LegendEntryIndicatorWrapperProps>`
  display: flex;
  align-items: center;
  border-radius: 1px;
  height: 14px;
  width: 14px;
  margin-right: 10px;
  ${(props) => props.borderColor && `border: 1px solid ${props.borderColor};`}
  ${(props) => props.fillColor && `background-color: ${props.fillColor};`}
`;

const LegendEntryIndicator = styled.div<LegendEntryIndicatorProps>`
  display: inline-block;

  width: 14px;
  ${(props: LegendEntryIndicatorProps) => `
    background-color: ${props.color};
    height: ${props.height ?? 2}px;
  `};
`;

const SubjectPureNameWithOptionalFee = ({
  name,
  fee,
  subject,
  isBenchmark,
}: {
  subject: StudioRequestSubject;
  isBenchmark: boolean;
  name: string;
  fee: number | undefined;
}) => {
  const superscripts = useBlockSubjectSuperscripts({ subject, isBenchmark });
  return <PureNameWithOptionalFee name={name} fee={fee} superscripts={superscripts} />;
};
export const LegendEntry: FC<React.PropsWithChildren<SeriesProps>> = ({
  iconType,
  customIcon,
  color = 'white',
  name = '',
  legend,
  borderColor,
  fillColor,
  height,
  fee,
  fontSize,
  subject,
  isBenchmark,
}) => {
  return (
    <LegendEntryWrapper fontSize={fontSize}>
      {customIcon ??
        (iconType ? (
          <Icon type={iconType} style={{ color, padding: 4 }} />
        ) : (
          <LegendEntryIndicatorWrapper
            className="legend-entry-indicator-wrapper"
            borderColor={borderColor}
            fillColor={fillColor}
          >
            <LegendEntryIndicator color={color} height={height} />
          </LegendEntryIndicatorWrapper>
        ))}
      {legend ||
        (!isNil(subject) && !isNil(isBenchmark) ? (
          <SubjectPureNameWithOptionalFee name={name} fee={fee} subject={subject} isBenchmark={isBenchmark} />
        ) : (
          <PureNameWithOptionalFee name={name} fee={fee} />
        ))}
    </LegendEntryWrapper>
  );
};

export default LegendEntry;
